<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <span class="flex-grow-1" v-if="title"><b>{{ $t('staticInfoIntro') }}</b> {{title}}</span>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <div class="d-flex flex-column">
                    <!-- Image -->
                    <div ckass="col-12">
                        <GalleryPicker class="gallery-picker" v-model="staticInfo.image" :ratioWidth="16" :ratioHeight="9" v-bind:class="{'is-invalid': errorValidator('Image') === false }" :max="1"></GalleryPicker>
                    </div>
                    <!-- Titles -->
                    <div role="group" class="form-group col-12 p-0">
                        <label class=""> {{ $t('titleMandatory') }}</label>
                        <CInput type="text" v-model="staticInfo.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(staticInfo.titles) && errorValidator('Titles')"></CInput>
                    </div>
                    <!-- Tabs -->
                    <div class="d-flex align-items-center col-12 p-0">
                        <label> {{ $t('tabs') }}</label>
                        <button type="button" class="btn mx-2 btn-add-tab" @click="addTab">{{ $t('addTab') }}</button>
                        <button type="button" class="btn btn-remove-tab" @click="removeCurrentTab">{{ $t('removeTab') }}</button>
                        <!-- Language dropdown -->
                        <div class="d-flex flex-grow-1 justify-content-end">
                            <LanguageDropdown class="align-self-end" v-model="currentLanguage"></LanguageDropdown>
                        </div>
                    </div>
                    <CTabs class="col-12 p-0 mt-3" v-if="staticInfo.tabs && staticInfo.tabs.length" variant="pills" @update:activeTab="tabChanged">
                        <CTab v-for="(tab, index) in staticInfo.tabs" :key="index" :title="$t('tab') + ' ' + (index + 1)">
                            <div class="d-flex flex-column mt-2">
                                <!-- Title -->
                                <div role="group" class="form-group col-12 mb-0">
                                    <label class=""> {{ $t('titleMandatory') }}</label>
                                    <CInput type="text" v-model="tab.titles[currentLanguage.key]" :invalid-feedback="errorOutput.Tabs[index].Titles" :is-valid="genericValidator(tab.titles) && errorValidator.Tabs[index].Titles"></CInput>
                                </div>
                                <!-- Contents -->
                                <div role="group" class="form-group col-12 mb-0">
                                    <label class=""> {{ $t('descriptionMandatory') }}</label>
                                    <vue-editor v-model="tab.contents[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(tab.contents) == false || errorValidator(`Tabs[${index}].Descriptions`) }"></vue-editor>
                                    <div v-if="errorOutput.Tabs[index].Contents" class="invalid-feedback">{{errorOutput.Tabs[index].Contents}}</div>
                                </div>
                            </div>
                        </CTab>
                    </CTabs>
                </div>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end mt-3"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary col-2 align-self-end mt-3" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { API_HELPER } from "@/helpers/api.js";
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import StaticInfo from "@/classes/StaticInfo";
import StaticInfoTab from "@/classes/StaticInfoTab";
import { mapState, mapActions } from 'vuex'
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import router from '@/router/index';
import { VueEditor } from "vue2-editor";
import FormLoading from '@/controls/FormLoading.vue'

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, GalleryPicker, VueEditor, FormLoading },
    props:{    
        baseStaticInfo:
        {
            type: Object,
            default: null,
        },
        baseStaticInfoTitle:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            staticInfo: new StaticInfo({}, []),
            currentTab: 0,
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
        }
    },
    mounted: function()
    {
        this.resetStaticInfo();
    },
    computed:
    {
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate Title
            for (const [key, value] of Object.entries(this.staticInfo.titles)) {
                if(!value)
                {
                    result['Titles'] = this.$i18n.t("validateBothTranslations");
                    break;
                };
            }

            // Validate Title and Content on Tabs
            result.Tabs = [];
            const self = this;

            this.staticInfo.tabs.forEach(function(tab){
                let error = {};

                for (const [key, value] of Object.entries(tab.titles)) {
                    if(!value)
                    {
                        error['Titles'] = self.$i18n.t("validateBothTranslations");
                        break;
                    };
                }

                for (const [key, value] of Object.entries(tab.contents)) {
                    if(!value)
                    {
                        error['Contents'] = self.$i18n.t("validateBothTranslations");
                        break;
                    };
                }

                result.Tabs.push(error);
            });
            // console.log(result)
            return result;
        },
        ...mapState('staticInfos', ['postStaticInfoOutput']),
        ...mapState('staticInfos', ['putStaticInfoOutput']),
        title: function()
        {
            return this.baseStaticInfo || this.baseStaticInfoTitle ? (this.baseStaticInfo ? this.baseStaticInfo.title : this.baseStaticInfoTitle) : null;
        }
    },
    methods:{
        resetStaticInfo(){
            if(!this.baseStaticInfo)
            {
                var names = {};
                CONSTANTS.supportedLanguages.forEach(language => names[language.key] = "");

                this.staticInfo = new StaticInfo(names, []);
            }
            else
            {
                this.staticInfo = deepClone(new StaticInfo, this.baseStaticInfo);      
                this.staticInfo.image = this.staticInfo.image ? {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.staticInfo.image])} : null; 
            }
        },
        submitClicked(){
            this.triedSubmit = true;
            // Emit event
            if(this.staticInfo.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.staticInfo);
            }
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push({name: 'StaticInfosList'});
        },
        addTab()
        {
            var titles = {};
            var contents = {};

            CONSTANTS.supportedLanguages.forEach(language => { titles[language.key] = ""; contents[language.key] = "" });

            this.staticInfo.tabs.push(new StaticInfoTab(titles, contents));
        },
        removeCurrentTab()
        {
            this.staticInfo.tabs.splice(this.currentTab, 1);
        },
        tabChanged(val)
        {
            this.currentTab = val;
        }
    },
    watch:
    {
        postStaticInfoOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        putStaticInfoOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseStaticInfo: function()
        {
            this.resetStaticInfo();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

    .gallery-picker
    {
        margin: 2rem auto;
    }

    .btn-add-tab, .btn-remove-tab
    {
        background-color: var(--primary);
        color: var(--tertiary);
    }
</style> 